import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Video, Copy, Check, Clock, CheckCircle, X, AlertCircle } from 'lucide-react';
import AsyncInterviewModal from './AsyncInterviewModal';

const statusStyle = {
  scheduled: { label: 'Scheduled',  color: 'bg-blue-50 text-blue-700 border-blue-200',   icon: Clock },
  open:      { label: 'Awaiting',   color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  live:      { label: 'Live now',   color: 'bg-red-50 text-red-700 border-red-200',      icon: AlertCircle },
  completed: { label: 'Completed',  color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
  expired:   { label: 'Expired',    color: 'bg-gray-50 text-gray-500 border-gray-200',    icon: X },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const AsyncInterviewCard = ({ applicationId, candidate, recruiterId, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!applicationId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/async-interviews/by-application/${applicationId}`);
      if (res.data?.success) setItems(res.data.data || []);
    } catch (e) {
      console.error('fetch async interviews', e);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const copyLink = async (item) => {
    try {
      await navigator.clipboard.writeText(item.meet_link);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {}
  };

  return (
    <Card data-testid="async-interview-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Video className="w-4 h-4 text-blue-600" />
            Video Interviews
          </CardTitle>
          <button
            onClick={() => setModalOpen(true)}
            className="text-xs text-blue-600 hover:underline"
            data-testid="async-new-btn"
          >
            + New
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && <p className="text-xs text-gray-400">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="text-center py-4">
            <p className="text-xs text-gray-500">No video interviews yet.</p>
            <button
              onClick={() => setModalOpen(true)}
              className="mt-2 text-xs text-blue-600 hover:underline"
              data-testid="async-empty-btn"
            >
              Send a video interview
            </button>
          </div>
        )}
        {items.map((it) => {
          const st = statusStyle[it.status] || statusStyle.scheduled;
          const Icon = st.icon;
          return (
            <div key={it.id} className="border border-gray-200 rounded-lg p-3 space-y-1.5" data-testid={`async-item-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-gray-800 truncate">{it.title}</p>
                <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md border ${st.color} flex-shrink-0`}>
                  <Icon className="w-2.5 h-2.5" />
                  {st.label}
                </span>
              </div>
              <p className="text-[11px] text-gray-500">
                {it.question_count} question{it.question_count !== 1 ? 's' : ''} · opens {fmtDate(it.opens_at)} · expires {fmtDate(it.expires_at)}
              </p>

              {/* Question list — always visible so recruiter can see what was asked */}
              {it.questions && it.questions.length > 0 && it.status !== 'completed' && (
                <ul className="mt-1.5 space-y-1" data-testid={`async-qs-${it.id}`}>
                  {it.questions.map((q, qi) => (
                    <li key={qi} className="flex items-start gap-1.5 text-[11px] text-gray-700">
                      <span className="text-[10px] font-semibold text-gray-400 mt-px flex-shrink-0">Q{qi + 1}</span>
                      <span className="line-clamp-2 leading-snug">
                        {q.prompt_text}
                        {q.video_url && <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-medium uppercase text-purple-600 bg-purple-50 px-1 py-px rounded">video</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Per-question response playback */}
              {it.status === 'completed' && it.responses && it.responses.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {it.responses.map((r, ri) => {
                    const q = (it.questions || [])[r.order] || {};
                    return (
                      <details key={ri} className="text-[11px] border border-gray-100 rounded">
                        <summary className="px-2 py-1.5 cursor-pointer text-gray-700 hover:bg-gray-50 flex items-start gap-1.5">
                          <span className="font-semibold text-gray-500 flex-shrink-0">Q{r.order + 1}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block truncate text-gray-800">{q.prompt_text || `Question ${r.order + 1}`}</span>
                            <span className="block text-[10px] text-gray-400 mt-0.5">
                              {Math.round(r.duration_s || 0)}s{q.video_url ? ' · with video prompt' : ''}
                            </span>
                          </span>
                        </summary>
                        {q.video_url && (
                          <div className="px-2 pt-2">
                            <p className="text-[10px] font-medium text-purple-700 mb-1">Interviewer&apos;s video prompt</p>
                            <video src={q.video_url} controls className="w-full bg-black rounded" />
                          </div>
                        )}
                        {r.video_url && (
                          <div className="px-2 pt-2">
                            <p className="text-[10px] font-medium text-gray-600 mb-1">Candidate response</p>
                            <video src={r.video_url} controls className="w-full bg-black rounded" />
                          </div>
                        )}
                        {r.transcript && (
                          <p className="px-2 py-1.5 text-gray-500 leading-relaxed">{r.transcript}</p>
                        )}
                      </details>
                    );
                  })}
                </div>
              )}
              {(it.status === 'completed' || it.response_count > 0) && (
                <p className="text-[11px] text-gray-600">
                  {it.response_count} response{it.response_count !== 1 ? 's' : ''}
                </p>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => copyLink(it)}
                  className="text-[11px] text-blue-600 hover:underline flex items-center gap-1"
                  data-testid={`async-copy-${it.id}`}
                >
                  {copiedId === it.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiedId === it.id ? 'Copied' : 'Copy link'}
                </button>
                {it.status === 'completed' && (
                  <a href={`#async-${it.id}`} className="text-[11px] text-blue-600 hover:underline">View responses</a>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>

      <AsyncInterviewModal
        open={modalOpen}
        onOpenChange={(o) => { if (!o) setModalOpen(false); }}
        candidate={candidate}
        recruiterId={recruiterId}
        onCreated={() => { fetchItems(); onChanged?.(); }}
      />
    </Card>
  );
};

export default AsyncInterviewCard;
